/**
 * Funnel Steps Panel - MA-FE-076 + MA-UI-117 + MA-UI-118
 * 
 * Simplified step navigator with working drag-and-drop.
 * 
 * MA-UI-118 Visual Refinements:
 * - Muted indigo with soft gradients (desaturated, blends with dark UI)
 * - Inner sheen/highlight on button (inset shadow)
 * - True sliding toggle with transform-based motion
 * - Reduced toggle contrast (secondary to button)
 * - 8px spacing between controls (space-y-2)
 * - Embedded feel - controls look native to the surface
 * 
 * MA-UI-117 Updates:
 * - Premium pill toggle for Canvas/Visualization modes
 * - Elevated "+ New Step" as primary CTA (above toggle)
 * - Vertical stack layout: New Step button on top, toggle below
 * 
 * Key fixes applied:
 * 1. Drag handle always visible (not opacity-0)
 * 2. Added z-index to dragging element
 * 3. Fixed arrayMove to use correct indices
 * 4. Removed interfering CSS transitions from drag handle
 */

import React, { useState, useMemo, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Search,
  MessageSquare,
  TextCursor,
  Type,
  FileCheck,
  Calendar,
  GripVertical,
  Loader2,
  ListTree,
  Pencil,
  Check,
  X,
  GitBranch,
  LayoutGrid,
} from "lucide-react";
import { cn } from "@/lib/utils";

// =============================================================================
// Node Type Configuration
// MA-FE-077: Unified Response Step - replaces separate Message and Question
// =============================================================================
const NODE_TYPE_CONFIG = {
  response: {
    label: "Response",
    icon: MessageSquare,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/15",
  },
  input: {
    label: "Input",
    icon: Type,
    color: "text-purple-400",
    bgColor: "bg-purple-500/15",
  },
  consent: {
    label: "Consent",
    icon: FileCheck,
    color: "text-amber-400",
    bgColor: "bg-amber-500/15",
  },
  booking: {
    label: "Booking",
    icon: Calendar,
    color: "text-blue-400",
    bgColor: "bg-blue-500/15",
  },
  // Legacy types - mapped to response styling for backward compatibility
  message: {
    label: "Response",  // MA-FE-077: Show as Response
    icon: MessageSquare,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/15",
  },
  question: {
    label: "Response",  // MA-FE-077: Show as Response
    icon: MessageSquare,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/15",
  },
};

// =============================================================================
// Sortable Step Item - Simple pattern from NodeListReorder
// =============================================================================
/**
 * SortableStepItem - Drag-and-drop sortable step
 * 
 * Matches NodeListReorder.SortableRow pattern exactly:
 * - Transform/transition applied to outer div
 * - Drag handle always visible (no opacity-0)
 * - z-index: 50 when dragging to ensure visibility above siblings
 * - No conflicting CSS transitions on the drag handle
 */
function SortableStepItem({
  node,
  stepNumber,
  isSelected,
  isHighlighted,
  isEntry,
  isSubmitStep,  // MA-UI-112: Lead submission step indicator
  onSelect,
  onDeleteClick,
  onRenameClick,
  isRenaming,
  renameValue,
  onRenameChange,
  onRenameSubmit,
  onRenameCancel,
}) {
  const config = NODE_TYPE_CONFIG[node.node_type] || NODE_TYPE_CONFIG.message;
  const Icon = config.icon;
  
  const label = node.metadata?.label || config.label;
  const shortLabel = label.length > 20 ? label.substring(0, 19) + "…" : label;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  // Style exactly matching NodeListReorder pattern + z-index for visibility
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : "auto",
    position: isDragging ? "relative" : undefined,
  };

  // Handle key press in rename input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onRenameSubmit?.();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onRenameCancel?.();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group"
      data-node-id={node.id}
    >
      <div
        onClick={() => !isRenaming && onSelect?.(node.id)}
        className={cn(
          "flex items-center gap-2 h-9 px-2 rounded-lg",
          "text-left transition-colors duration-150",
          !isRenaming && "cursor-pointer",
          // Selected state
          isSelected && [
            "bg-indigo-500/15",
            "border-l-2 border-l-indigo-400 -ml-0.5 pl-[9px]"
          ],
          // Highlighted state
          !isSelected && isHighlighted && [
            "bg-indigo-500/10",
            "ring-1 ring-indigo-400/40",
          ],
          !isSelected && !isHighlighted && !isRenaming && "hover:bg-white/5",
          // Dragging state - clear visual feedback
          isDragging && "ring-2 ring-indigo-500/60 bg-slate-800 shadow-lg",
          // Renaming state
          isRenaming && "bg-slate-800/80 ring-1 ring-indigo-500/50"
        )}
        data-testid={`step-item-${node.id}`}
      >
        {/* Drag Handle - ALWAYS VISIBLE (no opacity-0) - matches NodeListReorder */}
        <button
          type="button"
          className={cn(
            "flex items-center justify-center w-5 h-5 rounded",
            "text-slate-500 hover:text-slate-300 hover:bg-white/10",
            isDragging ? "cursor-grabbing" : "cursor-grab"
          )}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          data-testid={`drag-handle-${node.id}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        {/* Step Number */}
        <span className="w-4 text-xs text-white/40 text-right flex-shrink-0">
          {stepNumber}
        </span>

        {/* Step Icon */}
        <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0">
          <Icon className={cn("h-3.5 w-3.5", config.color)} />
        </div>

        {/* Label or Rename Input */}
        {isRenaming ? (
          <div className="flex-1 flex items-center gap-1">
            <Input
              value={renameValue}
              onChange={(e) => onRenameChange?.(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="h-6 text-xs bg-slate-900 border-slate-600 text-slate-100 px-2"
              onClick={(e) => e.stopPropagation()}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
              onClick={(e) => {
                e.stopPropagation();
                onRenameSubmit?.();
              }}
            >
              <Check className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-slate-400 hover:text-slate-300 hover:bg-white/10"
              onClick={(e) => {
                e.stopPropagation();
                onRenameCancel?.();
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <>
            {/* Label */}
            <span className={cn(
              "flex-1 text-sm truncate",
              isSelected ? "text-slate-100 font-medium" : "text-slate-300"
            )}>
              {shortLabel}
            </span>
            
            {/* ENTRY badge */}
            {isEntry && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400 uppercase tracking-wide flex-shrink-0">
                Entry
              </span>
            )}
            
            {/* MA-UI-112: SUBMIT badge for lead submission step */}
            {isSubmitStep && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 uppercase tracking-wide flex-shrink-0">
                Submit
              </span>
            )}
            
            {/* MA-FUNNEL-OUTCOME-STD-002: END badge for terminal nodes */}
            {node.content?.is_terminal && (
              <span className={cn(
                "text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0",
                node.content.outcome === "qualified" ? "bg-green-500/20 text-green-400" :
                node.content.outcome === "not_qualified" ? "bg-red-500/20 text-red-400" :
                node.content.outcome === "abandoned" ? "bg-amber-500/20 text-amber-400" :
                node.content.outcome === "no_consent" ? "bg-orange-500/20 text-orange-400" :
                node.content.outcome === "error" ? "bg-slate-500/20 text-slate-400" :
                "bg-indigo-500/20 text-indigo-400"
              )} data-testid={`end-badge-${node.id}`}>
                End
              </span>
            )}

            {/* Rename button - visible on hover only */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-5 w-5 flex-shrink-0",
                "text-slate-600 hover:text-indigo-400 hover:bg-transparent",
                "opacity-0 group-hover:opacity-100 transition-opacity"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onRenameClick?.(node.id);
              }}
              data-testid={`rename-step-${node.id}`}
            >
              <Pencil className="h-3 w-3" />
            </Button>

            {/* Delete button - visible on hover only */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-5 w-5 flex-shrink-0",
                "text-slate-600 hover:text-red-400 hover:bg-transparent",
                "opacity-0 group-hover:opacity-100 transition-opacity"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick?.(node.id);
              }}
              data-testid={`delete-step-${node.id}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Main Panel Component
// =============================================================================
export default function FunnelStepsPanel({
  nodes = [],
  selectedNodeId,
  entryNodeId,
  highlightedNodeId,
  onSelectNode,
  onAddNode,
  onDeleteNode,
  onReorderNodes,
  onRenameNode,
  isMutating = false,
  // MA-UI-110: Visualization mode toggle
  isVisualizationMode = false,
  onToggleVisualization,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = React.useRef(null);
  const [deleteNodeId, setDeleteNodeId] = useState(null);
  const [renamingNodeId, setRenamingNodeId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  // Sensors - same as NodeListReorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Filter nodes by search
  const filteredNodes = useMemo(() => {
    if (!searchQuery.trim()) return nodes;
    const query = searchQuery.toLowerCase();
    return nodes.filter(node => {
      const label = node.metadata?.label || "";
      const type = node.node_type || "";
      return label.toLowerCase().includes(query) || type.toLowerCase().includes(query);
    });
  }, [nodes, searchQuery]);

  // Node IDs for sortable context
  const nodeIds = useMemo(() => filteredNodes.map(n => n.id), [filteredNodes]);

  // Show search only when > 7 steps
  const showSearch = nodes.length > 7;

  // Handle drag end - Fixed to work correctly with both filtered and unfiltered lists
  // When search is active, we need to find the actual indices in the full `nodes` array
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    // Find indices in the FULL nodes array (not the filtered one)
    const oldIndex = nodes.findIndex(n => n.id === active.id);
    const newIndex = nodes.findIndex(n => n.id === over.id);
    
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(nodes, oldIndex, newIndex);
    onReorderNodes?.(reordered);
  }, [nodes, onReorderNodes]);

  // Delete confirmation
  const handleConfirmDelete = useCallback(() => {
    if (deleteNodeId) {
      onDeleteNode?.(deleteNodeId);
      setDeleteNodeId(null);
    }
  }, [deleteNodeId, onDeleteNode]);

  // Rename handlers
  const handleRenameClick = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setRenamingNodeId(nodeId);
      setRenameValue(node.metadata?.label || "");
    }
  }, [nodes]);

  const handleRenameSubmit = useCallback(() => {
    if (renamingNodeId && renameValue.trim()) {
      onRenameNode?.(renamingNodeId, renameValue.trim());
    }
    setRenamingNodeId(null);
    setRenameValue("");
  }, [renamingNodeId, renameValue, onRenameNode]);

  const handleRenameCancel = useCallback(() => {
    setRenamingNodeId(null);
    setRenameValue("");
  }, []);

  // Get node to delete for confirmation message
  const nodeToDelete = deleteNodeId ? nodes.find(n => n.id === deleteNodeId) : null;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-3 py-[11px] border-b border-white/10">
        <div className="flex items-center justify-between mb-0">
          <div className="flex items-center gap-2">
            <ListTree className="h-4 w-4 text-indigo-400" />
            <h3 className="text-sm font-medium text-slate-200">Steps</h3>
            <span className="text-xs text-slate-500">({nodes.length})</span>
          </div>
          {showSearch && !searchOpen && (
            <button
              onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 50); }}
              className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-slate-300 transition-colors"
              data-testid="steps-search-toggle"
            >
              <Search className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        
        {/* Search - click to expand */}
        {showSearch && searchOpen && (
          <div className="relative mt-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              ref={searchRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => { if (!searchQuery.trim()) setSearchOpen(false); }}
              placeholder="Search steps..."
              className="h-7 pl-7 text-xs bg-slate-800/50 border-slate-700/50"
            />
          </div>
        )}
      </div>

      {/* Steps List */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {filteredNodes.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No steps</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={nodeIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-0.5">
                {filteredNodes.map((node) => {
                  const stepNumber = nodes.findIndex(n => n.id === node.id) + 1;
                  
                  return (
                    <SortableStepItem
                      key={node.id}
                      node={node}
                      stepNumber={stepNumber}
                      isSelected={node.id === selectedNodeId}
                      isHighlighted={node.id === highlightedNodeId && node.id !== selectedNodeId}
                      isEntry={node.id === entryNodeId}
                      isSubmitStep={node.metadata?.is_submit_step === true}
                      onSelect={onSelectNode}
                      onDeleteClick={setDeleteNodeId}
                      onRenameClick={handleRenameClick}
                      isRenaming={node.id === renamingNodeId}
                      renameValue={renameValue}
                      onRenameChange={setRenameValue}
                      onRenameSubmit={handleRenameSubmit}
                      onRenameCancel={handleRenameCancel}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* MA-UI-118: Premium Control Stack - Refined visual hierarchy */}
      {/* Spacing: 8px gap (space-y-2), aligned left edges, embedded feel */}
      <div className="flex-shrink-0 p-3 border-t border-white/10 space-y-2">
        
        {/* MA-UI-118: New Step Button - PRIMARY CTA */}
        {/* Muted indigo with soft gradient, no hard edges, unified surface */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className={cn(
                "w-full h-9 text-sm font-medium",
                // Muted indigo gradient - desaturated, blends with dark UI
                "bg-gradient-to-b from-indigo-500/90 to-indigo-600/90",
                // Soft outer glow only - no inner highlights or borders
                "shadow-md shadow-indigo-500/20",
                "text-white border-0",
                "rounded-lg",
                // Hover: slightly brighter but still muted
                "hover:from-indigo-500/95 hover:to-indigo-600/95",
                "hover:shadow-lg hover:shadow-indigo-500/25",
                "transition-all duration-150"
              )}
              disabled={isMutating}
              data-testid="add-step-btn"
            >
              {isMutating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  New Step
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-44">
            {/* MA-PPB-FUNNEL-GUARDRAILS-115: Removed 'consent' - deprecated standalone step type */}
            {['response', 'input', 'booking'].map((type) => {
              const config = NODE_TYPE_CONFIG[type];
              return (
                <DropdownMenuItem
                  key={type}
                  onClick={() => onAddNode?.(type)}
                  className="gap-2"
                >
                  <config.icon className={cn("h-4 w-4", config.color)} />
                  {config.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* MA-UI-118: Toggle - SECONDARY control with true sliding thumb */}
        {/* Lower contrast than button, sliding motion, embedded feel */}
        <div 
          className={cn(
            "relative flex items-center h-7 p-0.5 rounded-full",
            // Subtle background - very low contrast, embedded
            "bg-slate-800/60",
            // No heavy border - just subtle definition
            "ring-1 ring-slate-700/40"
          )}
        >
          {/* Sliding thumb - true motion element */}
          {/* Uses same muted indigo as button for consistency */}
          <div 
            className={cn(
              "absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-full",
              // Muted indigo matching button - slightly less prominent
              "bg-gradient-to-b from-indigo-500/80 to-indigo-600/80",
              // Subtle elevation
              "shadow-sm shadow-indigo-500/20",
              // Smooth sliding motion
              "transition-transform duration-200 ease-out",
              // Transform-based sliding (not left/right positioning)
              isVisualizationMode 
                ? "translate-x-[calc(100%+2px)]" 
                : "translate-x-0"
            )}
            style={{ left: '2px' }}
          />
          
          {/* Canvas label - static position, thumb moves underneath */}
          <button
            type="button"
            onClick={() => isVisualizationMode && onToggleVisualization?.()}
            className={cn(
              "relative z-10 flex-1 flex items-center justify-center gap-1.5 h-full rounded-full",
              "text-[11px] font-medium transition-all duration-200",
              !isVisualizationMode 
                ? "text-white" 
                : "text-slate-500 hover:text-slate-400"
            )}
            data-testid="toggle-canvas-btn"
          >
            <LayoutGrid className="h-3 w-3" />
            <span>Canvas</span>
          </button>
          
          {/* Visualization label - static position, thumb moves underneath */}
          <button
            type="button"
            onClick={() => !isVisualizationMode && onToggleVisualization?.()}
            className={cn(
              "relative z-10 flex-1 flex items-center justify-center gap-1.5 h-full rounded-full",
              "text-[11px] font-medium transition-all duration-200",
              isVisualizationMode 
                ? "text-white" 
                : "text-slate-500 hover:text-slate-400"
            )}
            data-testid="toggle-visualization-btn"
          >
            <GitBranch className="h-3 w-3" />
            <span>Visualization</span>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteNodeId} onOpenChange={() => setDeleteNodeId(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Delete Step</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Are you sure you want to delete "{nodeToDelete?.metadata?.label || 'this step'}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 hover:bg-slate-700 border-slate-600 text-slate-200">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
